/**
 * Copyright 2026 OpenStack Foundation
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * */

import * as React from "react";
import T from "i18n-react/dist/i18n-react";
import {isBoolean} from "lodash";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {visuallyHidden} from "@mui/utils";
import {DEFAULT_PER_PAGE, FIFTY_PER_PAGE, TWENTY_PER_PAGE} from "../../../utils/constants";
import showConfirmDialog from "../showConfirmDialog";
import styles from "./mui-table.module.less";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PropTypes from "prop-types";

const ARCHIVED_CELL_SX = {
  backgroundColor: "background.light",
  color: "text.disabled"
};

const MuiTable = ({
                    columns = [],
                    data = [],
                    children,
                    totalRows,
                    perPage,
                    currentPage,
                    onPageChange,
                    onPerPageChange,
                    onSort,
                    options = {sortCol: "", sortDir: 1, disableProp: null}, // disableProp is the prop that will disable the row
                    getName = (item) => item.name,
                    onEdit,
                    onArchive,
                    onDelete,
                    onSelect,
                    canDelete = () => true,
                    deleteDialogTitle = null,
                    deleteDialogBody = null,
                    deleteDialogConfirmText = null,
                    confirmButtonColor = null
                  }) => {
  const totalColumnsCount =
    columns.length + (onEdit ? 1 : 0) + (onDelete ? 1 : 0) + (onArchive ? 1 : 0) + (onSelect ? 1 : 0);

  const handleChangePage = (_, newPage) => {
    onPageChange(newPage + 1);
  };

  const handleChangeRowsPerPage = (ev) => {
    onPerPageChange(ev.target.value);
  };

  const basePerPageOptions = [
    DEFAULT_PER_PAGE,
    TWENTY_PER_PAGE,
    FIFTY_PER_PAGE
  ];

  const initialPerPage = React.useRef(perPage);

  let customPerPageOptions = basePerPageOptions.includes(initialPerPage.current)
    ? basePerPageOptions
    : [...basePerPageOptions, initialPerPage.current].sort((a, b) => a - b);

  // remove per page selection if no action passed
  if (!onPerPageChange) {
    customPerPageOptions = [initialPerPage.current];
  }

  const {sortCol, sortDir} = options;

  const getArchivedCellSx = (row) =>
    options.disableProp && row[options.disableProp] ? ARCHIVED_CELL_SX : null;

  const getCellSx = (row, baseSx = {}) => ({
    ...baseSx,
    ...(getArchivedCellSx(row) || {})
  });

  const handleDelete = async (item) => {
    const isConfirmed = await showConfirmDialog({
      title: deleteDialogTitle || T.translate("general.are_you_sure"),
      text:
        typeof deleteDialogBody === "function"
          ? deleteDialogBody(getName(item))
          : deleteDialogBody ||
          `${T.translate("general.row_remove_warning")} ${getName(item)}`,
      type: "warning",
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor || "#DD6B55",
      confirmButtonText:
        deleteDialogConfirmText || T.translate("general.yes_delete")
    });

    if (isConfirmed) {
      onDelete(item.id);
    }
  };

  const renderCell = (row, col) => {
    if (col.render) {
      return col.render(row);
    }

    if (isBoolean(row[col.columnKey])) {
      return row[col.columnKey] ? (
        <CheckIcon fontSize="large"/>
      ) : (
        <CloseIcon fontSize="large"/>
      );
    }

    return row[col.columnKey];
  };

  return (
    <Box sx={{width: "100%"}}>
      <Paper elevation={0} sx={{width: "100%", mb: 2}}>
        <TableContainer
          component={Paper}
          sx={{borderRadius: 0, boxShadow: "none"}}
        >
          <Table>
            {/* TABLE HEADER */}
            <TableHead sx={{backgroundColor: "#EAEAEA"}}>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.columnKey}
                    sx={{
                      width: col.width,
                      minWidth: col.width,
                      maxWidth: col.width
                    }}
                    align={col.align ?? "left"}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortCol === col.columnKey}
                        direction={
                          sortCol === col.columnKey && sortDir === -1
                            ? "desc"
                            : "asc"
                        }
                        onClick={() => onSort(col.columnKey, sortDir * -1)}
                      >
                        {col.header}
                        {sortCol === col.columnKey ? (
                          <Box component="span" sx={visuallyHidden}>
                            {sortDir === -1
                              ? T.translate("mui_table.sorted_desc")
                              : T.translate("mui_table.sorted_asc")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      col.header
                    )}
                  </TableCell>
                ))}
                {onEdit && <TableCell sx={{width: 40}}/>}
                {onArchive && <TableCell sx={{width: 80}}/>}
                {onDelete && <TableCell sx={{width: 40}}/>}
                {onSelect && <TableCell sx={{width: 40}}/>}
              </TableRow>
            </TableHead>

            {/* TABLE BODY */}
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id}>
                  {/* Main content columns */}
                  {columns.map((col) => (
                    <TableCell
                      key={col.columnKey}
                      align={col.align ?? "left"}
                      className={`${
                        col.dottedBorder && styles.dottedBorderLeft
                      } ${col.className}`}
                      sx={getCellSx(row)}
                    >
                      {renderCell(row, col)}
                    </TableCell>
                  ))}
                  {/* Edit column */}
                  {onEdit && (
                    <TableCell
                      align="center"
                      className={styles.dottedBorderLeft}
                      sx={getCellSx(row, {width: 40})}
                    >
                      <IconButton size="large" onClick={() => onEdit(row)} data-testid="action-edit">
                        <EditIcon fontSize="large"/>
                      </IconButton>
                    </TableCell>
                  )}
                  {/* Archive column */}
                  {onArchive && (
                    <TableCell
                      align="center"
                      sx={{width: 80}}
                      className={styles.dottedBorderLeft}
                    >
                      <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        onClick={() => onArchive(row)}
                        data-testid="action-archive"
                        sx={{
                          fontSize: "1.3rem",
                          fontWeight: 500,
                          lineHeight: "2.2rem",
                          padding: "4px 5px"
                        }}
                      >
                        {row.is_archived
                          ? T.translate("general.unarchive")
                          : T.translate("general.archive")}
                      </Button>
                    </TableCell>
                  )}
                  {/* Delete column */}
                  {onDelete && (
                    <TableCell
                      align="center"
                      className={styles.dottedBorderLeft}
                      sx={getCellSx(row, {width: 40})}
                    >
                      {canDelete(row) && (
                        <IconButton
                          size="large"
                          onClick={() => handleDelete(row)}
                          data-testid="action-delete"
                        >
                          <DeleteIcon fontSize="large"/>
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                  {onSelect && (
                    <TableCell
                      align="center"
                      sx={{width: 40}}
                      className={styles.dottedBorderLeft}
                    >
                      <IconButton
                        size="large"
                        onClick={() => onSelect(row)}
                        data-testid="action-select"
                      >
                        <ArrowForwardIcon/>
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {/* Here we inject extra rows passed as children */}
              {children}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={totalColumnsCount} align="center">
                    {T.translate("mui_table.no_items")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        {perPage && currentPage && (
          <TablePagination
            component="div"
            count={totalRows}
            rowsPerPageOptions={customPerPageOptions}
            rowsPerPage={perPage}
            page={currentPage - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={T.translate("mui_table.rows_per_page")}
            sx={{
              ".MuiTablePagination-toolbar": {
                alignItems: "baseline",
                marginTop: "1.6rem"
              },
              ".MuiTablePagination-spacer": {
                display: "none"
              },
              ".MuiTablePagination-displayedRows": {
                marginLeft: "auto"
              }
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

MuiTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  children: PropTypes.node,
  totalRows: PropTypes.number,
  perPage: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onPerPageChange: PropTypes.func,
  onSort: PropTypes.func,
  options: PropTypes.object,
  getName: PropTypes.func,
  onEdit: PropTypes.func,
  onArchive: PropTypes.func,
  onDelete: PropTypes.func,
  onSelect: PropTypes.func,
  canDelete: PropTypes.func,
  deleteDialogTitle: PropTypes.string,
  deleteDialogBody: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  deleteDialogConfirmText: PropTypes.string,
  confirmButtonColor: PropTypes.string
};

export default MuiTable;
